import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import useStyles from './styles'
import theme from 'theme'
import schema from './schema'
import * as service from 'service'

const ConsentDetailsForm = ({
  setLoading,
  users,
  usersRefresh,
  consentForm,
  consentFormRefresh,
}) => {
  const classes = useStyles()

  const snackbar = useSnackbar()
  const { control, errors, reset, handleSubmit } = useForm({
    validationSchema: schema,
    defaultValues: {
      responsibleUsers: (users || []).filter((user) =>
        (consentForm?.responsibleIds || []).includes(user.id),
      ),
      emailConfirmation: consentForm?.emailConfirmation || false,
      emailResponsible: consentForm?.emailResponsible || false,
    },
  })

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const { responsibleUsers, ...restData } = data

      await service.dponet.consent.updateResponsible({
        consentFormId: consentForm?.id,
        ...restData,
        responsibleIds: (responsibleUsers || []).map((user) => user.id),
      })

      snackbar.open({
        message: 'Responsáveis atualizados com sucesso!',
        variant: 'success',
      })
      reset()
      usersRefresh()
      consentFormRefresh()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error.response.data.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      component="form"
      id="assign-responsible-consent-details-form"
      display="flex"
      flexDirection="column"
      gridGap={theme.spacing(3)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={10} className={classes.titleContainer}>
          <Typography variant="h4" className={classes.title}>
            {consentForm?.title}
          </Typography>
          <Typography variant="caption">{consentForm?.subtitle}</Typography>
        </Grid>
        <Grid item xs={12} md={10}>
          <Typography variant="h4">Atribuir Responsáveis</Typography>
          <Typography variant="caption" color="secondary">
            Altera os perfis responsáveis por gerenciar as revogações dos
            consentimentos de titulares de dados.
          </Typography>
        </Grid>
      </Grid>
      <Box p={2} bgcolor={theme.palette.background.default}>
        <Typography variant="h6">
          Encaminhar para os responsáveis da organização
        </Typography>
        <Typography>
          Indique quem irá operacionalizar as revogações nos consentimentos de
          titulares de dados dentro da sua organização. Todos os responsáveis
          recebem as mesmas notificações.
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gridGap={theme.spacing(1)}>
        <Controller
          as={
            <Autocomplete
              multiple
              fullWidth
              filterSelectedOptions
              options={users || []}
              getOptionLabel={(option) => option.name || ''}
              getOptionSelected={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Responsáveis"
                  placeholder="Selecione os responsáveis"
                  error={!!errors?.responsibleUsers}
                  helperText={errors?.responsibleUsers?.message}
                />
              )}
            />
          }
          control={control}
          name="responsibleUsers"
          onChange={([, data]) => data}
          mode="onChange"
        />

        <Controller
          name="emailConfirmation"
          control={control}
          onChange={([event]) => event.target.checked}
          as={
            <FormControlLabel
              label="Ativar a confirmação de identidade"
              control={<Switch color="primary" />}
            />
          }
        />

        <Controller
          name="emailResponsible"
          control={control}
          onChange={([event]) => event.target.checked}
          as={
            <FormControlLabel
              label="Ativar o envio de e-mail ao responsável pela solicitação"
              control={<Switch color="primary" />}
            />
          }
        />
      </Box>
    </Box>
  )
}

ConsentDetailsForm.propTypes = {
  setLoading: PropTypes.func.isRequired,
  users: PropTypes.array,
  usersRefresh: PropTypes.func.isRequired,
  consentForm: PropTypes.object,
  consentFormRefresh: PropTypes.func.isRequired,
}

export default ConsentDetailsForm
